import { complianceNavigationFace, systemName, tenantID } from '@/router/route-constants.ts';
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalizedGeneric,
  type RouteLocationRaw,
  type RouteRecordInfo,
} from 'vue-router';
import {
  facilitiesAccessible,
  facilitiesDetails,
  facilitiesInitialized,
  facilityLastVisited,
  userDefaultFacilityID,
} from '@/services/facilities';
import type { Facility } from '../../compliance-shared/types/common';
import type { PermitIndexID, Task, TaskGenerator, TaskTemplate } from '../../compliance-shared/types/compliance';
import type { FacilityID, TenantID } from '../../cross-app-shared/types/db';
import { computed, toValue, watch } from 'vue';
import { confirmModal } from '@/components/modals/ConfirmModal.vue';
import { auth, loginState, userLoginStateConfirmedPromise } from '../../cross-app-frontend/firebase';
import { facilitiesList } from '../../cross-app-frontend/facilities';
import { isJsonEqual } from '../../compliance-shared/services/json-stable-stringify.ts';

export interface RouteNamedMap {
  'My Tasks': RouteRecordInfo<'My Tasks', '/:tenantID/tasks', { tenantID: TenantID }, { tenantID: string }, never>;
  'My Calendar': RouteRecordInfo<
    'My Calendar',
    '/:tenantID/calendar',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Facility Tasks': RouteRecordInfo<
    'Facility Tasks',
    '/:tenantID/facility/tasks',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Facility Calendar': RouteRecordInfo<
    'Facility Calendar',
    '/:tenantID/facility/calendar',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Facility Permit Indexing': RouteRecordInfo<
    'Facility Permit Indexing',
    '/:tenantID/facility/permit-indexing',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Permit Indexing Editor': RouteRecordInfo<
    'Permit Indexing Editor',
    '/:tenantID/facility/permit-indexing/:permitIndexID/edit',
    { tenantID: TenantID; permitIndexID: PermitIndexID },
    { tenantID: string; permitIndexID: string },
    never
  >;
  'Permit Indexing Viewer': RouteRecordInfo<
    'Permit Indexing Viewer',
    '/:tenantID/facility/permit-indexing/:permitIndexID/view',
    { tenantID: TenantID; permitIndexID: PermitIndexID },
    { tenantID: string; permitIndexID: string },
    never
  >;
  'Permit Indexing Conditional Conditions': RouteRecordInfo<
    'Permit Indexing Conditional Conditions',
    '/:tenantID/facility/permit-indexing/conditional',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Schedule Tasks': RouteRecordInfo<
    'Schedule Tasks',
    '/:tenantID/facility/schedule',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'View Scheduled Task': RouteRecordInfo<
    'View Scheduled Task',
    '/:tenantID/facility/schedule/:taskGenerator',
    { tenantID: TenantID; taskGenerator: TaskGenerator['taskGeneratorID'] },
    { tenantID: string; taskGenerator: string },
    never
  >;
  'Add Generator From Template': RouteRecordInfo<
    'Add Generator From Template',
    '/:tenantID/facility/schedule/add',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Add Generator From Scratch': RouteRecordInfo<
    'Add Generator From Scratch',
    '/:tenantID/facility/schedule/create',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Reassign Owner': RouteRecordInfo<
    'Reassign Owner',
    '/:tenantID/facility/schedule/reassign',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Remove Involved': RouteRecordInfo<
    'Remove Involved',
    '/:tenantID/facility/schedule/involve-remove',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Add Involved': RouteRecordInfo<
    'Add Involved',
    '/:tenantID/facility/schedule/involve',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Suggested Tasks': RouteRecordInfo<
    'Suggested Tasks',
    '/:tenantID/facility/suggested',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  Folders: RouteRecordInfo<
    'Folders',
    '/:tenantID/facility/folders/:folderOrTaskIds*',
    { tenantID: TenantID; folderOrTaskIds?: string[] },
    { tenantID: string; folderOrTaskIds: string[] },
    never
  >;
  'Task Templates': RouteRecordInfo<
    'Task Templates',
    '/:tenantID/templates/:taskTemplateID?',
    { tenantID: TenantID; taskTemplateID?: TaskTemplate['taskTemplateID'] },
    { tenantID: string; taskTemplateID?: string },
    never
  >;
  'Facility Setup': RouteRecordInfo<
    'Facility Setup',
    '/:tenantID/facility/:facilityID/setup',
    { tenantID: TenantID; facilityID: FacilityID },
    { tenantID: string; facilityID: string },
    never
  >;
  Emails: RouteRecordInfo<'Emails', '/:tenantID/emails', { tenantID: TenantID }, { tenantID: string }, never>;
  Queue: RouteRecordInfo<'Queue', '/:tenantID/queue', { tenantID: TenantID }, { tenantID: string }, never>;
  Help: RouteRecordInfo<'Help', '/:tenantID/help', { tenantID: TenantID }, { tenantID: string }, never>;
  Login: RouteRecordInfo<'Login', '/login', Record<never, never>, Record<never, never>, never>;
  Facilities: RouteRecordInfo<
    'Facilities',
    '/:tenantID/facilities',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  Users: RouteRecordInfo<'Users', '/:tenantID/users', { tenantID: TenantID }, { tenantID: string }, never>;
  Unauthorized: RouteRecordInfo<
    'Unauthorized',
    '/:tenantID/unauthorized',
    { tenantID: TenantID },
    { tenantID: string },
    never
  >;
  'Not Found': RouteRecordInfo<'Not Found', '/:pathMatch(.*)*', Record<never, never>, Record<never, never>, never>;
}

declare module 'vue-router' {
  interface TypesConfig {
    RouteNamedMap: RouteNamedMap;
  }
}

function propsParse(route: RouteLocationNormalizedGeneric): object {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(route.params)) {
    if ((key === 'facilityID' || key === 'tenantID') && typeof value === 'string' && /^\d+$/.test(value)) {
      result[key] = parseInt(value, 10);
    } else {
      result[key] = value;
    }
  }
  return result;
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/:tenantID/tasks',
      name: 'My Tasks',
      component: () => import('@/pages/MyTasksPage.vue'),
    },
    {
      path: '/:tenantID/calendar',
      name: 'My Calendar',
      component: () => import('@/pages/MyTasksPage.vue'),
    },
    {
      path: '/:tenantID/facility/tasks',
      name: 'Facility Tasks',
      component: () => import('@/pages/FacilityTasksPage.vue'),
      meta: {
        useFacilities: true,
      },
    },
    {
      path: '/:tenantID/facility/calendar',
      name: 'Facility Calendar',
      component: () => import('@/pages/FacilityTasksPage.vue'),
      meta: {
        useFacilities: true,
      },
    },
    {
      path: '/:tenantID/facility/permit-indexing',
      name: 'Facility Permit Indexing',
      component: () => import('@/pages/FacilityPermitIndexingPage.vue'),
      props: propsParse,
      meta: {
        useFacilities: true,
      },
    },
    {
      path: '/:tenantID/facility/permit-indexing/:permitIndexID/edit',
      name: 'Permit Indexing Editor',
      component: () => import('@/pages/PermitIndexingEditorPage.vue'),
      props: propsParse,
      meta: {
        useFacilities: true,
      },
    },
    {
      path: '/:tenantID/facility/permit-indexing/:permitIndexID/view',
      name: 'Permit Indexing Viewer',
      component: () => import('@/pages/PermitIndexingEditorPage.vue'),
      props: propsParse,
      meta: {
        useFacilities: true,
      },
    },
    {
      path: '/:tenantID/facility/permit-indexing/conditional',
      name: 'Permit Indexing Conditional Conditions',
      component: () => import('@/pages/PermitIndexingConditionalConditionsPage.vue'),
      props: propsParse,
      meta: {
        useFacilities: true,
      },
    },
    {
      path: '/:tenantID/facility/schedule',
      component: () => import('@/pages/ScheduleTasksPage.vue'),
      props: propsParse,
      meta: {
        useFacilities: true,
      },
      children: [
        {
          path: '',
          name: 'Schedule Tasks',
          component: () => import('@/pages/schedule-tasks/TaskGeneratorTablePage.vue'),
          props: propsParse,
        },
        {
          path: ':taskGenerator',
          name: 'View Scheduled Task',
          component: () => import('@/pages/schedule-tasks/ScheduleTasksEditPage.vue'),
          props: propsParse,
        },
        {
          path: 'add',
          name: 'Add Generator From Template',
          component: () => import('@/pages/schedule-tasks/TaskGeneratorAddFromTemplate.vue'),
          props: propsParse,
        },
        {
          path: 'create',
          name: 'Add Generator From Scratch',
          component: () => import('@/pages/schedule-tasks/TaskGeneratorAddFromScratch.vue'),
          props: propsParse,
        },
        {
          path: 'reassign',
          name: 'Reassign Owner',
          component: () => import('@/pages/schedule-tasks/TaskOwnerReassignPage.vue'),
          props: propsParse,
        },
        {
          path: 'involve-remove',
          name: 'Remove Involved',
          component: () => import('@/pages/schedule-tasks/TaskInvolvedRemovePage.vue'),
          props: propsParse,
        },
        {
          path: 'involve',
          name: 'Add Involved',
          component: () => import('@/pages/schedule-tasks/TaskInvolvedAddPage.vue'),
          props: propsParse,
        },
      ],
    },
    {
      path: '/:tenantID/facility/suggested',
      name: 'Suggested Tasks',
      component: () => import('@/pages/SuggestedTasksPage.vue'),
      meta: {
        useFacilities: true,
      },
    },
    {
      path: '/:tenantID/facility/folders/:folderOrTaskIds*',
      name: 'Folders',
      component: () => import('@/pages/FacilityFoldersPage.vue'),
      props: propsParse,
      meta: {
        useFacilities: true,
      },
    },
    {
      path: '/:tenantID/templates/:taskTemplateID?',
      name: 'Task Templates',
      component: () => import('@/pages/TaskTemplatesPage.vue'),
      props: propsParse,
    },
    {
      path: '/:tenantID/facility/:facilityID/setup',
      name: 'Facility Setup',
      component: () => import('@/pages/FacilitySetupPage.vue'),
      props: propsParse,
    },
    {
      path: '/:tenantID/emails',
      name: 'Emails',
      component: () => import('@/pages/EmailLogPage.vue'),
    },
    {
      path: '/:tenantID/queue',
      name: 'Queue',
      component: () => import('@/pages/QueuePage.vue'),
    },
    {
      path: '/:tenantID/help',
      name: 'Help',
      component: () => import('@/pages/HelpPage.vue'),
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('../../cross-app-frontend/LoginPage.vue'),
    },
    {
      path: '/:tenantID/users',
      name: 'Users',
      component: () => import('../../cross-app-frontend/users/UsersPage.vue'),
    },
    {
      path: '/:tenantID/facilities',
      name: 'Facilities',
      component: () => import('../../cross-app-frontend/facility/FacilitiesPage.vue'),
      props: () => {
        const facility = facilitiesDetails[facilityIDFromRouteOrUserDefault.value!] ?? null;
        return {
          facility,
        };
      },
    },
    { path: '/:tenantID/unauthorized', name: 'Unauthorized', component: () => import('@/pages/UnauthorizedPage.vue') },
    { path: '/:pathMatch(.*)*', name: 'Not Found', component: () => import('@/pages/NotFoundPage.vue') },
  ],
});

export default router;

router.beforeEach(async (to, from): Promise<boolean | string | RouteLocationRaw<keyof RouteNamedMap>> => {
  await userLoginStateConfirmedPromise;

  if (loginState.value !== 'logged-in' && to.name !== 'Login') {
    return { name: 'Login', query: { returnTo: to.fullPath }, replace: true };
  }

  if (loginState.value === 'logged-in' && to.name === 'Login') {
    // Refresh the session cookie so that image downloads and such work:
    const idToken = await auth.currentUser?.getIdToken();
    if (idToken) {
      await fetch('/api/crossapp/set-session-cookie', {
        method: 'POST',
        body: idToken,
      });
    }

    if (
      typeof to.query.returnTo === 'string' &&
      to.query.returnTo.startsWith('/') &&
      !to.query.returnTo.startsWith('//') &&
      !to.query.returnTo.startsWith('/\\')
    ) {
      window.location.href = to.query.returnTo;
      return new Promise(() => {}); // If we `return false`, vue-router will throw an error.
    }

    return {
      name: 'My Tasks',
      params: {
        tenantID,
      },
    };
  }

  if ('tenantID' in to.params && to.params.tenantID === '0') {
    await facilitiesInitialized;
    // You might think we should use facilitiesAccessible here, but those are only the facilities within the current
    // tenant. Since this is a fallback, we need the full list of facilities across all tenants:
    const tenantID = facilitiesList.value[0]?.tenantID;
    if (!tenantID) {
      return { replace: true, name: 'Not Found' };
    }

    // We force a real navigation because the app expects tenantID to be initialized as a constant from the route
    // on startup:
    window.location.href = router.resolve({
      name: to.name,
      params: {
        ...to.params,
        tenantID,
      },
      query: to.query,
    }).fullPath;
    return new Promise(() => {}); // If we `return false`, vue-router will throw an error.
  }

  if (to.matched.some((route) => route.meta.useFacilities)) {
    // Filter out any facilities this person doesn't have access to:
    if (to.query.facilities) {
      if (to.query.facilities !== 'all') {
        const queryValidated = facilityIdListToQueryParam(facilitiesQueryToIdList(to.query.facilities as string));
        if (queryValidated !== to.query.facilities && !isAllFacilitiesRoute(to.name)) {
          to.query.facilities = queryValidated;
          return to;
        }
      }
      // Copy over query params from the prior route if none were explicitly provided:
    } else if (from.query.facilities && to.query.facilities === undefined) {
      to.query.facilities = from.query.facilities;
      return to;
      // If we have no params at all, default to the user's default facility:
    } else {
      await facilitiesInitialized;
      if (userDefaultFacilityID.value) {
        to.query.facilities = facilityIdListToQueryParam([userDefaultFacilityID.value]);
        return to;
        // If they don't have a default facility, they must not have access to anything, send them to unauthorized:
      } else {
        return { replace: true, name: 'Unauthorized', params: { tenantID: tenantID || (0 as TenantID) } };
      }
    }
    // If the user selected multiple facilities, carry that multiple selection forward to other pages, even if they
    // don't necessarily use those filters:
  } else if (from.query.facilities && from.query.facilities.includes(',') && !to.query.facilities) {
    to.query.facilities = from.query.facilities;
    return to;
  }

  const currentPageComponent = router.currentRoute.value.matched[0]?.instances.default;
  if (currentPageComponent && 'isDirty' in currentPageComponent && to.path !== from.path) {
    const isDirty = toValue(currentPageComponent.isDirty);
    if (isDirty) {
      const stayOnPage = await confirmModal({
        title: 'Unsaved changes',
        message: 'You have unsaved changes, are you sure you want to leave this page?',
        choices: [
          { label: 'Discard changes', value: false, icon: 'fa-trash', variant: 'destructive' },
          { label: 'Stay here', value: true, icon: 'fa-anchor', variant: 'default' },
        ],
        defaultCloseValue: true,
      });
      if (stayOnPage) {
        return false;
      }
    }
  }

  if (to.path === '/') {
    if (complianceNavigationFace === 'compliance') {
      return {
        replace: true,
        name: 'My Tasks',
        params: {
          tenantID,
        },
      };
    } else {
      return {
        replace: true,
        name: 'Facility Permit Indexing',
        params: {
          tenantID,
        },
      };
    }
  }

  const taskIDMatch = /^\/(\d+)\/(task_.+)$/.exec(to.path);
  if (taskIDMatch) {
    (await import('@/components/tasks/TaskDetailsModal.vue')).taskDetailsModalOpen({
      taskID: taskIDMatch[2] as Task['taskID'],
      fake: false,
    });
    return {
      replace: true,
      name: 'My Tasks',
      params: {
        tenantID: Number(taskIDMatch[1]) as TenantID,
      },
    };
  }

  if ('facilityID' in to.params && to.params?.facilityID) {
    await facilitiesInitialized;
    if (facilitiesAccessible.facilities.length === 0 && to.path !== '/unauthorized') {
      return { replace: true, name: 'Unauthorized' };
    }

    const paramsFacilityID = to.params.facilityID;
    if (paramsFacilityID === 'default' || paramsFacilityID === 'undefined') {
      to.params.facilityID = String(userDefaultFacilityID.value!);
      return to;
    } else if (
      typeof paramsFacilityID === 'string' &&
      !facilitiesAccessible.facilities.find((f) => f.facilityID === Number(paramsFacilityID))
    ) {
      to.params.facilityID = String(userDefaultFacilityID.value!);
      return to;
    }
  }

  return true;
});

router.afterEach((to) => {
  const routeName = to.name?.toString() || '';
  if ('facilityID' in to.params && to.params?.facilityID) {
    const facilityName = facilitiesDetails[Number(to.params?.facilityID) as Facility['facilityID']]?.name;
    if (facilityName) {
      document.title = routeName.replace('Facility ', '') + ' | ' + facilityName + ` | ${systemName} | Toolkit`;
    } else {
      document.title = routeName + ` | ${systemName} | Toolkit`;
    }
  } else if (routeName) {
    document.title = routeName + ` | ${systemName} | Toolkit`;
  } else {
    document.title = systemName + ' | Toolkit';
  }

  facilityLastVisited.value = facilityIDFromRouteOrUserDefault.value;

  // Force a refresh if the tenantID changes, as Firestore is initialized with the tenantID from the first loaded route:
  if ('tenantID' in to.params && String(to.params.tenantID) !== String(tenantID)) {
    window.location.href = router.resolve(to).href;
  }
});

watch(loginState, (loginState, oldState) => {
  if (oldState === 'logged-in' && loginState !== 'logged-in') {
    let query = {};
    if (router.currentRoute.value.name !== 'Login') {
      query = { returnTo: router.currentRoute.value.fullPath };
    }
    router.push({ name: 'Login', query });
  }
});

/**
 * Priority:
 * 1. Facility route param (ex: /facilities/1/import)
 * 2. First facility in query string (ex: /facility/tasks?facilities=l95rmupi_24cuop2vkzt,l95rmubj_020oisqvnqz)
 * 3. The facility the user last visited, if still accessible (stored in localStorage)
 * 4. The facility the user is associated with by default on the user document, if accessible.
 * 5. The first facility in their accessible facilities list.
 * 6. null
 */
export const facilityIDFromRouteOrUserDefault = computed((): Facility['facilityID'] | null => {
  const accessible = new Set(facilitiesAccessible.facilities.map((f) => f.facilityID));
  const routeParam =
    'facilityID' in router.currentRoute.value.params && router.currentRoute.value.params.facilityID
      ? (Number(router.currentRoute.value.params.facilityID) as Facility['facilityID'])
      : undefined;
  if (routeParam && accessible.has(routeParam)) {
    return routeParam;
  }

  const routeQuery = router.currentRoute.value.query.facilities;
  if (typeof routeQuery === 'string') {
    const maybeFacilityID = Number(routeQuery.split(',')[0]);
    if (facilitiesAccessible.facilities.some((f) => f.facilityID === maybeFacilityID)) {
      return maybeFacilityID as Facility['facilityID'];
    }
  }

  return userDefaultFacilityID.value;
});

function returnOldIfEqual<T>(oldValue: T | undefined, newValue: T): T {
  if (oldValue && isJsonEqual(oldValue, newValue)) {
    return oldValue;
  } else {
    return newValue;
  }
}

export const facilitiesViewingFromRoute = computed((oldValue): Facility['facilityID'][] => {
  const accessible: Set<Facility['facilityID']> = isAllFacilitiesRoute(router.currentRoute.value.name)
    ? new Set(Object.values(facilitiesDetails).map((f) => f.facilityID))
    : new Set(facilitiesAccessible.facilities.map((f) => f.facilityID));
  const viewing = new Set<Facility['facilityID']>();

  const routeParam =
    'facilityID' in router.currentRoute.value.params && router.currentRoute.value.params.facilityID
      ? (Number(router.currentRoute.value.params.facilityID) as Facility['facilityID'])
      : undefined;
  if (routeParam && accessible.has(routeParam)) {
    viewing.add(routeParam);
  }

  const routeQuery = router.currentRoute.value.query.facilities;
  if (typeof routeQuery === 'string') {
    if (routeQuery === 'all') {
      return returnOldIfEqual(oldValue, Array.from(accessible));
    }

    const unvalidatedFacilities = routeQuery.split(',').map((id) => Number(id)) as Facility['facilityID'][];
    for (const facilityID of unvalidatedFacilities) {
      if (accessible.has(facilityID)) {
        viewing.add(facilityID);
      }
    }
  }

  return returnOldIfEqual(oldValue, Array.from(viewing));
});

function facilityIdListToQueryParam(ids: Facility['facilityID'][]): string {
  return ids.join(',');
}

function facilitiesQueryToIdList(routeQuery: string): Facility['facilityID'][] {
  if (routeQuery === 'all') {
    return facilitiesAccessible.facilities.map((f) => f.facilityID);
  }

  const accessible = new Set(facilitiesAccessible.facilities.map((f) => f.facilityID));
  const unvalidatedFacilities = routeQuery.split(',').map((f) => Number(f)) as Facility['facilityID'][];
  return unvalidatedFacilities.filter((f) => accessible.has(f));
}

export function routerFacilityReplaceOrAdd(newId: Facility['facilityID']) {
  const accessible = facilitiesAccessible.facilities.some((f) => f.facilityID === newId);
  if (!accessible) {
    return;
  }

  let currentList = facilitiesViewingFromRoute.value;
  if (currentList.length <= 1) {
    currentList = [newId];
  } else {
    currentList = [newId].concat(currentList.filter((f) => f !== newId));
  }

  if ('facilityID' in router.currentRoute.value.params && router.currentRoute.value.params.facilityID) {
    const query = { ...router.currentRoute.value.query };
    if (query.facilities === 'all') {
      // Do nothing, we're good-to-go.
    } else if (currentList.length === 1) {
      delete query.facilities;
    } else {
      query.facilities = facilityIdListToQueryParam(currentList);
    }

    return router.replace({
      params: {
        ...router.currentRoute.value.params,
        facilityID: newId,
      },
      query,
    });
  } else {
    return router.replace({
      query: {
        ...router.currentRoute.value.query,
        facilities: facilityIdListToQueryParam(currentList),
      },
    });
  }
}

export function routerFacilitiesSet(facilities: (Facility['facilityID'] | 'all')[]) {
  if ('facilityID' in router.currentRoute.value.params && router.currentRoute.value.params.facilityID) {
    console.warn('Tried to update facility list from a page that views a single facility.');
    return;
  }

  const query = { ...router.currentRoute.value.query };
  if (facilities.some((f) => f === 'all')) {
    query.facilities = 'all';
  } else {
    query.facilities = facilityIdListToQueryParam(facilities as Facility['facilityID'][]);
  }

  return router.replace({
    query,
  });
}

// On 2025-12-18 Corporate requested that all facilities be able to view all permits/conditions:
function isAllFacilitiesRoute(name: string | symbol | undefined) {
  return name === 'Facility Permit Indexing' || name === 'Permit Indexing Viewer';
}

export const isViewingAllFacilitiesRoute = computed((): boolean => {
  return isAllFacilitiesRoute(router.currentRoute.value.name);
});
